/**
 * DxfCadLoader - DXF 格式 CAD 加载器
 *
 * 职责：解析 .dxf 文件，通过 DxfParser 转换为 CadLoadResult
 * 取消策略：soft（解析完成后检查 signal）
 */

import { createCadLoadResultFromMetadata } from '@/plugins/cad/deserializeCadMetadata'
import { DxfParser } from '@/plugins/cad/module/parser/DxfParser'
import { createDefaultCadPlaneMetadata } from '@/plugins/cad/normalizeJsonCadMetadata'
import type { CadLoadRequest, CadLoadResult } from '@/plugins/cad/types'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

/** 取消级别：hard 表示可强制中断，soft 表示仅在完成后检查 */
export type CancellationLevel = 'hard' | 'soft'

/** 从 source 获取 DXF 文本数据 */
async function fetchDxfData(source: CadLoadRequest, signal?: AbortSignal): Promise<string> {
  if (source.data !== undefined) {
    return source.data
  }
  if (source.url) {
    const resp = await fetch(source.url, { signal })
    if (!resp.ok) {
      throw new Error(`DXF fetch failed: ${resp.status} ${resp.statusText}`)
    }
    return resp.text()
  }
  throw new Error('DxfCadLoader: data 和 url 均未提供')
}

/** DXF 格式 CAD 加载器 */
export class DxfCadLoader implements AssetLoader<CadLoadRequest, CadLoadResult> {
  /** 加载器支持的格式标识 */
  readonly format = 'dxf'
  /** 自动识别优先级，数值越大越先尝试 */
  readonly detectPriority = 0
  /** 取消级别：soft 表示仅在分步完成后检查 signal */
  readonly cancellationLevel: CancellationLevel = 'soft'

  /**
   * 检测来源是否为 DXF 格式。
   *
   * @param source - CAD 加载请求
   * @returns format 显式为 'dxf' 或 url 以 .dxf 结尾时返回 true
   */
  canLoad(source: CadLoadRequest): boolean {
    if (source.format === 'dxf') return true
    if (source.url && /\.dxf$/i.test(source.url)) return true
    return false
  }

  /**
   * 加载并解析 DXF 文件。
   *
   * @param source - CAD 加载请求，需提供 data 或 url
   * @param context - 加载上下文，用于取消与进度汇报
   * @returns CAD 加载结果
   */
  async load(source: CadLoadRequest, context: LoadContext): Promise<CadLoadResult> {
    const signal = source.signal ?? context.signal

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const text = await fetchDxfData(source, signal)

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const parsed = DxfParser.parse(text)
    if (!parsed) {
      throw new Error('DxfCadLoader: DXF 解析失败，数据无效')
    }

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    context.onProgress?.(1)
    return createCadLoadResultFromMetadata({
      layers: parsed.layers,
      entities: parsed.entities,
      plane: createDefaultCadPlaneMetadata(),
    })
  }
}
